import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import API_BASE from "../config";
import StatusBadge from "./StatusBadge";
import ConfirmDialog from "./ConfirmDialog";
import s from "./StudentDetailModal.module.css";

/* ── Document upload constraints ────────────────────────────────────────
   Defense-in-depth: the server is authoritative, but rejecting bad files
   early gives a faster UX and stops obvious abuse before bytes are sent.
   Both MIME and extension are checked because each can be spoofed alone. */
const DOC_MAX_BYTES = 10 * 1024 * 1024; // 10 MB
const DOC_ALLOWED_EXT = [
  ".pdf", ".jpg", ".jpeg", ".png", ".gif", ".webp",
  ".doc", ".docx", ".xls", ".xlsx",
];
const DOC_ALLOWED_MIME = new Set([
  "application/pdf",
  "image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
]);

/** Returns null if the file is acceptable, else a user-facing error message. */
function validateDocFile(file) {
  if (!file) return "Pick a file to upload.";
  if (file.size > DOC_MAX_BYTES) {
    return `File is ${(file.size / 1024 / 1024).toFixed(1)} MB — limit is 10 MB.`;
  }
  const name = (file.name || "").toLowerCase();
  const okExt = DOC_ALLOWED_EXT.some(ext => name.endsWith(ext));
  // Some browsers leave type empty for unknown files — fall back to extension.
  const okMime = !file.type || DOC_ALLOWED_MIME.has(file.type);
  if (!okExt || !okMime) {
    return "Unsupported file type. Accepted: PDF, JPG, PNG, GIF, WEBP, DOC(X), XLS(X).";
  }
  return null;
}

function fmtBytes(n) {
  if (n == null) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function fmtDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    year: "numeric", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}

/** Auth headers used for document mutations — mirrors the rest of the app. */
function authHeaders() {
  let user = null;
  try { user = JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user") || "null"); }
  catch { /* ignore */ }
  return {
    "X-User-Role":  user?.role     || "",
    "X-Username":   user?.username || "",
    ...(user?.linkedEntityId   != null ? { "X-Linked-Entity-Id":   String(user.linkedEntityId) } : {}),
    ...(user?.linkedEntityType         ? { "X-Linked-Entity-Type": user.linkedEntityType } : {}),
  };
}

/** Pure check — used to decide whether to render the Documents section.
 *  Documents include sensitive artefacts (birth cert, IDs, Form 137) so
 *  this is a visibility gate, not just a write gate.
 *
 *  Allowlist (must stay in sync with the backend gate in
 *  StudentDocumentController):
 *    - Owner, SuperAdmin, Admin  — system administrators
 *    - Registrar, RegistrarStaff — own student records day-to-day,
 *      uploading admission documents is part of their workflow.
 *  Cashier / CashierStaff are intentionally OUT — they handle tuition
 *  payments, not student records, and these documents can carry
 *  personally-identifiable data they shouldn't see. Teachers / Students
 *  / Parents are also out. */
function canManageStudentDocs() {
  let user = null;
  try { user = JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user") || "null"); }
  catch { /* ignore */ }
  const r = user?.role || "";
  return r === "Owner"
      || r === "SuperAdmin"
      || r === "SuperDev"
      || r === "Admin"
      || r === "Registrar"
      || r === "RegistrarStaff";
}

/** Pick an emoji "icon" for a file — purely cosmetic, content-type based. */
function docIcon(contentType, filename) {
  const ct = (contentType || "").toLowerCase();
  const name = (filename || "").toLowerCase();
  if (ct.startsWith("image/") || /\.(png|jpe?g|gif|webp)$/.test(name)) return "🖼️";
  if (ct === "application/pdf" || name.endsWith(".pdf"))               return "📄";
  if (ct.includes("word")      || /\.docx?$/.test(name))               return "📝";
  if (ct.includes("excel") || ct.includes("spreadsheet") || /\.xlsx?$/.test(name)) return "📊";
  return "📎";
}

const AVATAR_COLORS = ["#5a4e3a","#3a4e4a","#4a3a5a","#4e4a3a","#3a4a5a","#5a3a3a","#3a5a3a"];
function avatarColor(seed) {
  let h = 0;
  for (let i = 0; i < (seed || "").length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}
function initials(first, last) {
  return ((last?.[0] ?? "") + (first?.[0] ?? "")).toUpperCase() || "?";
}
function fullName(st) {
  return [st?.lastName, st?.firstName].filter(Boolean).join(", ") || "(Unnamed)";
}
function photoSrc(photoUrl) {
  if (!photoUrl) return null;
  if (/^https?:\/\//i.test(photoUrl)) return photoUrl;
  return `${API_BASE}${photoUrl.startsWith("/") ? "" : "/"}${photoUrl}`;
}
function fmtDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

/**
 * Premium student profile modal. Self-fetches the parent links + parent
 * details so it can be dropped in anywhere with just a `student`. Other
 * context arrays (sections, schoolYears, semesters, rooms, enrollments)
 * are accepted as props when the parent already has them — this avoids
 * a second wave of redundant fetches when opened from the academics
 * pages that already loaded that data.
 *
 * Read-only — to mutate enrollments / parents, admins go to Directory.
 *
 * Props:
 *   open         boolean
 *   student      Student row (must have id; ideally photoUrl, names, etc.)
 *   sections     optional Array<Section>
 *   gradeLevels  optional Array<GradeLevel>
 *   schoolYears  optional Array<SchoolYear>
 *   semesters    optional Array<Semester>
 *   rooms        optional Array<Room>
 *   enrollments  optional Array<Enrollment>     — full list (filtered here)
 *   onClose      fn()
 */
export default function StudentDetailModal({
  open,
  student,
  sections   = [],
  gradeLevels = [],
  schoolYears = [],
  semesters   = [],
  rooms       = [],
  enrollments = [],
  onClose,
}) {
  const [parentLinks, setParentLinks] = useState([]);
  const [parents, setParents]         = useState([]);
  const [loadingParents, setLoadingParents] = useState(false);

  // Self-fetched fallbacks so the modal renders the full "Current term"
  // (grade · section · homeroom) and "Enrollment history" sections from
  // any caller, even ones that don't pre-load enrollments or rooms.
  const [fetchedEnrollments, setFetchedEnrollments] = useState(null);
  const [fetchedRooms, setFetchedRooms]             = useState(null);

  // ── Documents (admin-gated) ──────────────────────────────────────────
  const showDocs = canManageStudentDocs();
  const [documents, setDocuments]       = useState([]);
  const [loadingDocs, setLoadingDocs]   = useState(false);
  const [docsError, setDocsError]       = useState("");
  const [addLabel, setAddLabel]         = useState("");
  const [addFile, setAddFile]           = useState(null);
  const [addFileError, setAddFileError] = useState("");
  const [addBusy, setAddBusy]           = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleteBusy, setDeleteBusy]       = useState(false);

  // Lock body scroll + ESC handler
  useEffect(() => {
    if (!open) return;
    function onKey(e) { if (e.key === "Escape") onClose?.(); }
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  // Fetch parent links + parent rows on open. Cancellation-safe.
  useEffect(() => {
    if (!open || !student?.id) return;
    let cancelled = false;
    setLoadingParents(true);
    setParentLinks([]); setParents([]);
    Promise.all([
      fetch(`${API_BASE}/api/parent-student?studentId=${student.id}`)
        .then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/parents`).then(r => r.ok ? r.json() : []),
    ]).then(([links, allParents]) => {
      if (cancelled) return;
      const linksArr = Array.isArray(links) ? links : [];
      const allArr   = Array.isArray(allParents) ? allParents : [];
      // Filter parents down to those referenced by this student's links.
      const linkedIds = new Set(linksArr.map(l => l.parentId));
      setParentLinks(linksArr);
      setParents(allArr.filter(p => linkedIds.has(p.id)));
    }).catch(() => { /* silent — empty state */ })
      .finally(() => { if (!cancelled) setLoadingParents(false); });
    return () => { cancelled = true; };
  }, [open, student?.id]);

  // Fetch documents on open (admin-gated). Cancellation-safe.
  useEffect(() => {
    if (!open || !student?.id || !showDocs) {
      setDocuments([]);
      return;
    }
    let cancelled = false;
    setLoadingDocs(true);
    setDocsError("");
    fetch(`${API_BASE}/api/students/${student.id}/documents`, {
      headers: authHeaders(),
    })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(rows => { if (!cancelled) setDocuments(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (!cancelled) setDocsError("Could not load documents."); })
      .finally(() => { if (!cancelled) setLoadingDocs(false); });
    return () => { cancelled = true; };
  }, [open, student?.id, showDocs]);

  // Self-fetch this student's enrollment history if the caller didn't pass
  // it in. Uses the backend's ?studentId= filter so the response is bounded
  // to a few rows even on a 2000-student database. Cancellation-safe.
  useEffect(() => {
    if (!open || !student?.id) return;
    if (Array.isArray(enrollments) && enrollments.length > 0) {
      setFetchedEnrollments(null);
      return;
    }
    let cancelled = false;
    fetch(`${API_BASE}/api/enrollments?studentId=${student.id}`, {
      headers: authHeaders(),
    })
      .then(r => r.ok ? r.json() : [])
      .then(rows => { if (!cancelled) setFetchedEnrollments(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (!cancelled) setFetchedEnrollments([]); });
    return () => { cancelled = true; };
  }, [open, student?.id, enrollments]);

  // Self-fetch the room list if the caller didn't pass it in. /api/rooms is
  // small (typically <100 rows) and powers the homeroom pill in Current term.
  useEffect(() => {
    if (!open || !student?.id) return;
    if (Array.isArray(rooms) && rooms.length > 0) {
      setFetchedRooms(null);
      return;
    }
    let cancelled = false;
    fetch(`${API_BASE}/api/rooms`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(rs => { if (!cancelled) setFetchedRooms(Array.isArray(rs) ? rs : []); })
      .catch(() => { if (!cancelled) setFetchedRooms([]); });
    return () => { cancelled = true; };
  }, [open, student?.id, rooms]);

  // Reset the add-form whenever the modal closes — never carry a half-filled
  // upload over to the next student that gets opened.
  useEffect(() => {
    if (open) return;
    setAddLabel(""); setAddFile(null); setAddFileError("");
    setAddBusy(false); setPendingDelete(null); setDeleteBusy(false);
  }, [open]);

  function handleFilePick(file) {
    setAddFile(file || null);
    if (!file) { setAddFileError(""); return; }
    setAddFileError(validateDocFile(file) || "");
    // Auto-fill the label with the filename if the user hasn't typed one yet.
    if (!addLabel.trim()) {
      const base = file.name.replace(/\.[^.]+$/, "");
      setAddLabel(base);
    }
  }

  async function uploadDocument(e) {
    e?.preventDefault?.();
    if (!showDocs || !student?.id || addBusy) return;
    const validation = validateDocFile(addFile);
    if (validation) { setAddFileError(validation); return; }
    const label = (addLabel || addFile.name).trim();
    if (!label) { setAddFileError("Please enter a label."); return; }

    setAddBusy(true);
    setDocsError("");
    try {
      const fd = new FormData();
      fd.append("file", addFile);
      fd.append("label", label);
      // Don't set Content-Type — the browser must add the multipart boundary.
      const res = await fetch(`${API_BASE}/api/students/${student.id}/documents`, {
        method: "POST",
        headers: authHeaders(),
        body: fd,
      });
      if (!res.ok) throw new Error(`Upload failed (HTTP ${res.status})`);
      const row = await res.json();
      setDocuments(prev => [...prev, row]);
      setAddLabel(""); setAddFile(null); setAddFileError("");
    } catch (err) {
      setDocsError(err?.message || "Upload failed.");
    } finally {
      setAddBusy(false);
    }
  }

  async function confirmDelete() {
    if (!pendingDelete || deleteBusy) return;
    setDeleteBusy(true);
    setDocsError("");
    try {
      const res = await fetch(`${API_BASE}/api/student-documents/${pendingDelete.id}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`Delete failed (HTTP ${res.status})`);
      const goneId = pendingDelete.id;
      setDocuments(prev => prev.filter(d => d.id !== goneId));
      setPendingDelete(null);
    } catch (err) {
      setDocsError(err?.message || "Delete failed.");
    } finally {
      setDeleteBusy(false);
    }
  }

  function downloadDocument(doc) {
    // Open in a new tab; the backend sends Content-Disposition: attachment
    // so the browser triggers a save dialog without navigating away.
    if (!doc?.id) return;
    const url = `${API_BASE}/api/student-documents/${doc.id}/download`;
    window.open(url, "_blank", "noopener,noreferrer");
  }

  // ── Derive context ────────────────────────────────────────────────
  // Prefer the caller's prop arrays (cheaper — already in memory); fall
  // back to the self-fetched results so the modal works from any caller.
  // Memoised so identity-stable arrays don't bust downstream useMemos.
  const effectiveEnrollments = useMemo(() => (
    (Array.isArray(enrollments) && enrollments.length > 0)
      ? enrollments
      : (fetchedEnrollments || [])
  ), [enrollments, fetchedEnrollments]);
  const effectiveRooms = useMemo(() => (
    (Array.isArray(rooms) && rooms.length > 0)
      ? rooms
      : (fetchedRooms || [])
  ), [rooms, fetchedRooms]);

  const sectionsById   = useMemo(() => Object.fromEntries(sections.map(r => [r.id, r])),  [sections]);
  const gradeById      = useMemo(() => Object.fromEntries(gradeLevels.map(r => [r.id, r])), [gradeLevels]);
  const syById         = useMemo(() => Object.fromEntries(schoolYears.map(r => [r.id, r])), [schoolYears]);
  const semById        = useMemo(() => Object.fromEntries(semesters.map(r => [r.id, r])),  [semesters]);
  const roomsById      = useMemo(() => Object.fromEntries(effectiveRooms.map(r => [r.id, r])), [effectiveRooms]);

  const myEnrollments = useMemo(() => {
    if (!student?.id) return [];
    return effectiveEnrollments
      .filter(e => String(e.studentId) === String(student.id))
      .sort((a, b) => (b.id || 0) - (a.id || 0));  // newest first
  }, [effectiveEnrollments, student?.id]);

  // Current term snapshot from the *most recent ENROLLED* row (or just
  // the latest row if none enrolled).
  const currentEnroll = useMemo(() => {
    const enrolled = myEnrollments.find(e => String(e.status || "").toUpperCase() === "ENROLLED");
    return enrolled || myEnrollments[0] || null;
  }, [myEnrollments]);

  // Section: prefer the enrollment's sectionId, then fall back to the
  // student's denormalised currentSectionId (kept in sync by the backend).
  // This means Current term still renders even if no enrollments are
  // available — e.g. when the modal is opened from the Tuition tabs.
  const currentSection = useMemo(() => {
    if (currentEnroll?.sectionId) return sectionsById[currentEnroll.sectionId] || null;
    if (student?.currentSectionId) return sectionsById[student.currentSectionId] || null;
    return null;
  }, [currentEnroll, student?.currentSectionId, sectionsById]);

  const currentGrade    = currentSection?.gradeLevelId ? gradeById[currentSection.gradeLevelId] : null;
  const currentHomeroom = currentSection?.homeroomId ? roomsById[currentSection.homeroomId] : null;
  const currentSy       = currentEnroll?.schoolYearId ? syById[currentEnroll.schoolYearId] : null;
  const currentSem      = currentEnroll?.semesterId ? semById[currentEnroll.semesterId] : null;

  // Sort parents: primary first, then alphabetical.
  const orderedParents = useMemo(() => {
    const primaryByParentId = new Map();
    for (const link of parentLinks) {
      primaryByParentId.set(link.parentId, !!link.primary);
    }
    return [...parents].sort((a, b) => {
      const aP = primaryByParentId.get(a.id) ? 0 : 1;
      const bP = primaryByParentId.get(b.id) ? 0 : 1;
      if (aP !== bP) return aP - bP;
      return ((a.lastName || "") + (a.firstName || "")).localeCompare(
             (b.lastName || "") + (b.firstName || ""));
    });
  }, [parents, parentLinks]);

  if (!open || !student) return null;

  const photo = photoSrc(student.photoUrl);
  const name  = fullName(student);

  return createPortal(
    <div
      className={s.backdrop}>
      <div
        className={s.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="sdm-title">
        <div className={s.head}>
          <div
            className={s.avatar}
            style={photo ? { backgroundImage: `url(${photo})` }
                         : { backgroundColor: avatarColor(name) }}
            aria-hidden="true">
            {!photo && initials(student.firstName, student.lastName)}
          </div>
          <div className={s.headMain}>
            <div className={s.eyebrow}>Student profile</div>
            <h3 id="sdm-title" className={s.title}>{name}</h3>
            <div className={s.metaRow}>
              {student.studentNumber && (
                <span className={s.numberPill}>{student.studentNumber}</span>
              )}
              <StatusBadge status={student.status} />
            </div>
          </div>
          <button
            type="button"
            className={s.closeBtn}
            onClick={onClose}
            aria-label="Close">
            ✕
          </button>
        </div>

        <div className={s.body}>
          {/* Current term — always shows when the student has a section,
              even if the enrollment row hasn't been loaded. Falls back to
              student.currentSectionId so Tuition/Reports/etc. callers (which
              don't pre-load enrollments) still see grade · section · room. */}
          {(currentEnroll || currentSection || student?.currentSectionId) && (
            <div className={s.section} style={{ animationDelay: "0ms" }}>
              <div className={s.sectionHead}>
                <span className={s.sectionTitle}>Current term</span>
                {currentSy?.label && (
                  <span className={s.sectionSub}>
                    SY {currentSy.label}
                    {currentSem?.label ? ` · ${currentSem.label}` : ""}
                  </span>
                )}
              </div>
              <div className={s.termRow}>
                {currentGrade && (
                  <span className={s.termPill}>
                    Grade <b>{currentGrade.name}</b>
                  </span>
                )}
                {currentSection && (
                  <span className={s.termPill}>
                    Section <b>{currentSection.name}</b>
                  </span>
                )}
                {currentHomeroom && (
                  <span className={s.termPill}>
                    Room <b>{currentHomeroom.name}</b>
                    {currentHomeroom.building ? ` · ${currentHomeroom.building}` : ""}
                  </span>
                )}
                {!currentSection && (
                  <span className={s.empty}>No current section assigned.</span>
                )}
              </div>
            </div>
          )}

          <div className={s.section} style={{ animationDelay: "60ms" }}>
            <div className={s.sectionHead}>
              <span className={s.sectionTitle}>Demographics</span>
            </div>
            <dl className={s.dl}>
              <div className={s.dlRow}>
                <dt className={s.dt}>Birthdate</dt>
                <dd className={s.dd}>{fmtDate(student.birthdate) || <em>—</em>}</dd>
              </div>
              <div className={s.dlRow}>
                <dt className={s.dt}>Sex</dt>
                <dd className={s.dd}>{student.sex || <em>—</em>}</dd>
              </div>
              <div className={s.dlRow}>
                <dt className={s.dt}>Contact</dt>
                <dd className={s.dd}>{student.contact || <em>—</em>}</dd>
              </div>
              <div className={s.dlRow}>
                <dt className={s.dt}>Address</dt>
                <dd className={s.dd}>{student.address || <em>—</em>}</dd>
              </div>
            </dl>
          </div>

          <div className={s.section} style={{ animationDelay: "120ms" }}>
            <div className={s.sectionHead}>
              <span className={s.sectionTitle}>Parents & guardians</span>
              <span className={s.sectionSub}>
                {loadingParents
                  ? "Loading…"
                  : `${orderedParents.length} on record`}
              </span>
            </div>
            {loadingParents ? (
              <div className={s.loading}>Loading parents…</div>
            ) : orderedParents.length === 0 ? (
              <div className={s.empty}>
                No parents linked to this student yet.
              </div>
            ) : (
              <div className={s.parentGrid}>
                {orderedParents.map(p => {
                  const link = parentLinks.find(l => l.parentId === p.id);
                  const role = link?.relationship || "Parent";
                  const primary = !!link?.primary;
                  const pName = [p.lastName, p.firstName].filter(Boolean).join(", ");
                  return (
                    <div key={p.id} className={s.parentCard}>
                      <div className={s.parentHead}>
                        <div className={s.parentName}>{pName || "(Unnamed)"}</div>
                        <span className={`${s.parentRole} ${primary ? s.parentRolePrimary : ""}`}>
                          {primary ? `${role} · Primary` : role}
                        </span>
                      </div>
                      <div className={s.parentMeta}>
                        {p.contact && <span>📞 {p.contact}</span>}
                        {p.email && (
                          <span>
                            ✉ <a href={`mailto:${p.email}`}>{p.email}</a>
                          </span>
                        )}
                        {p.occupation && <span>💼 {p.occupation}</span>}
                        {p.address && <span>🏠 {p.address}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {myEnrollments.length > 0 && (
            <div className={s.section} style={{ animationDelay: "180ms" }}>
              <div className={s.sectionHead}>
                <span className={s.sectionTitle}>Enrollment history</span>
                <span className={s.sectionSub}>
                  {myEnrollments.length} record{myEnrollments.length === 1 ? "" : "s"}
                </span>
              </div>
              <ul className={s.enrollList}>
                {myEnrollments.map(e => {
                  const sec = e.sectionId ? sectionsById[e.sectionId] : null;
                  const grade = sec?.gradeLevelId ? gradeById[sec.gradeLevelId] : null;
                  const sy  = e.schoolYearId ? syById[e.schoolYearId]  : null;
                  const sem = e.semesterId   ? semById[e.semesterId]   : null;
                  return (
                    <li key={e.id} className={s.enrollRow}>
                      <div className={s.enrollMain}>
                        <div className={s.enrollSection}>
                          {sec ? (grade ? `${grade.name} · ${sec.name}` : sec.name) : "—"}
                        </div>
                        <div className={s.enrollMeta}>
                          {sy?.label && `SY ${sy.label}`}
                          {sy?.label && sem?.label ? " · " : ""}
                          {sem?.label || ""}
                        </div>
                      </div>
                      <StatusBadge status={e.status} />
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* ── Documents (admin-gated) ────────────────────────────────
              Hidden for non-admin roles — these are sensitive artefacts
              (birth cert, IDs). Admins can add, download, and remove. */}
          {showDocs && (
            <div className={s.section} style={{ animationDelay: "240ms" }}>
              <div className={s.sectionHead}>
                <span className={s.sectionTitle}>Documents</span>
                <span className={s.sectionSub}>
                  {loadingDocs
                    ? "Loading…"
                    : `${documents.length} on file`}
                </span>
              </div>

              {docsError && <div className={s.docError}>{docsError}</div>}

              {loadingDocs ? (
                <div className={s.loading}>Loading documents…</div>
              ) : documents.length === 0 ? (
                <div className={s.empty}>
                  No documents attached yet. Upload one below.
                </div>
              ) : (
                <ul className={s.docList}>
                  {documents.map(d => (
                    <li key={d.id} className={s.docRow}>
                      <span className={s.docIcon} aria-hidden="true">
                        {docIcon(d.contentType, d.originalFilename)}
                      </span>
                      <div className={s.docMain}>
                        <div className={s.docLabel}>{d.label || d.originalFilename || "Document"}</div>
                        <div className={s.docMeta}>
                          {d.originalFilename && <span className={s.docFilename}>{d.originalFilename}</span>}
                          {d.sizeBytes != null && <span>{fmtBytes(d.sizeBytes)}</span>}
                          {d.uploadedAt && <span>{fmtDateTime(d.uploadedAt)}</span>}
                          {d.uploadedBy && <span>by {d.uploadedBy}</span>}
                        </div>
                      </div>
                      <div className={s.docActions}>
                        <button
                          type="button"
                          className={s.docDownload}
                          onClick={() => downloadDocument(d)}
                          aria-label={`Download ${d.label || "document"}`}>
                          Download
                        </button>
                        <button
                          type="button"
                          className={s.docDelete}
                          onClick={() => setPendingDelete(d)}
                          disabled={deleteBusy}
                          aria-label={`Delete ${d.label || "document"}`}>
                          Delete
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              {/* Upload form */}
              <form className={s.docUpload} onSubmit={uploadDocument}>
                <div className={s.docUploadRow}>
                  <label className={s.docField}>
                    <span className={s.docFieldLabel}>Label</span>
                    <input
                      type="text"
                      className={s.docInput}
                      value={addLabel}
                      onChange={e => setAddLabel(e.target.value)}
                      placeholder="e.g. Birth Certificate"
                      maxLength={120}
                      disabled={addBusy}
                    />
                  </label>
                  <label className={s.docField}>
                    <span className={s.docFieldLabel}>File</span>
                    <input
                      type="file"
                      className={s.docFileInput}
                      accept={DOC_ALLOWED_EXT.join(",")}
                      onChange={e => handleFilePick(e.target.files?.[0] || null)}
                      disabled={addBusy}
                    />
                  </label>
                </div>
                {addFile && !addFileError && (
                  <div className={s.docHint}>
                    {addFile.name} · {fmtBytes(addFile.size)}
                  </div>
                )}
                {addFileError && <div className={s.docError}>{addFileError}</div>}
                <div className={s.docUploadFoot}>
                  <span className={s.docHintMuted}>
                    PDF, JPG, PNG, GIF, WEBP, DOC(X), XLS(X) · max 10 MB
                  </span>
                  <button
                    type="submit"
                    className={s.docPrimary}
                    disabled={addBusy || !addFile || !!addFileError}>
                    {addBusy ? "Uploading…" : "+ Add document"}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>

        <div className={s.foot}>
          <button type="button" className={s.btnGhost} onClick={onClose}>
            Close
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={!!pendingDelete}
        tone="danger"
        title="Delete document?"
        description={pendingDelete?.label || pendingDelete?.originalFilename || ""}
        message="The document will no longer appear in this student's profile. This cannot be undone from the UI."
        confirmLabel={deleteBusy ? "Deleting…" : "Delete"}
        cancelLabel="Cancel"
        busy={deleteBusy}
        onConfirm={confirmDelete}
        onCancel={() => { if (!deleteBusy) setPendingDelete(null); }}
      />
    </div>,
    document.body
  );
}
